import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import isEqual from "lodash.isequal";
import * as React from "react";
import {useEffect, useRef, useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {Caption} from "@khanacademy/wonder-blocks-typography";
import {useMutation} from "@khan/data-gql";
import {Errors, KAError} from "@khan/logging";

import type {PlaybackRate} from "../types/account-settings-types.ts";

import {SET_VIDEO_SETTINGS_MUTATION} from "./set-video-settings-mutation.ts";
import StudentVideoSettings from "./student-settings-video.tsx";

interface VideoSettingsData {
    muteVideos: boolean;
    showCaptions: boolean;
    playbackRate: PlaybackRate;
}

interface Props {
    kaid: string;
    initialSettings: VideoSettingsData;
    successfullyChangedSettings: () => unknown;
}

export const VideoSettings: React.FC<Props> = ({
    kaid,
    initialSettings,
    successfullyChangedSettings,
}) => {
    const [currentSettings, setCurrentSettings] =
        useState<VideoSettingsData>(initialSettings);
    const [isUnexpectedError, setIsUnexpectedError] = useState(false);
    const isMounted = useRef(true);

    const [setVideoSettingsMutation, mutationResult] = useMutation(
        SET_VIDEO_SETTINGS_MUTATION,
    );

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const onSettingsChange = (newSettings: Partial<VideoSettingsData>) => {
        setIsUnexpectedError(false);
        setCurrentSettings((prev) => ({
            ...prev,
            ...newSettings,
        }));
    };

    const setSettings = async (settings: VideoSettingsData) => {
        const {muteVideos, showCaptions, playbackRate} = settings;

        setIsUnexpectedError(false);

        try {
            const result = await setVideoSettingsMutation({
                kaid,
                muteVideos,
                showCaptions,
                playbackRate,
            });

            if (!isMounted.current) {
                return;
            }

            if (result.status === "success") {
                const errors = result.data?.setSettings?.errors;
                if (errors && errors[0]) {
                    throw new KAError(errors[0].code, Errors.Internal);
                }

                successfullyChangedSettings();
            } else if (result.status === "error") {
                throw new KAError(
                    "Failed to set video settings",
                    Errors.Internal,
                    {
                        cause: result.error,
                    },
                );
            }
        } catch (error) {
            if (!isMounted.current) {
                return;
            }

            setIsUnexpectedError(true);
        }
    };

    const renderSaveButton = () => {
        const settings = {
            ...initialSettings,
            ...currentSettings,
        };

        const isSaveChangesDisabled =
            mutationResult?.status === "loading" ||
            isEqual(initialSettings, settings);

        return (
            <View style={styles.buttonContainer}>
                <Button
                    onClick={() => setSettings(settings)}
                    style={styles.button}
                    spinner={mutationResult?.status === "loading"}
                    disabled={isSaveChangesDisabled}
                >
                    {t`Save changes`}
                </Button>
                {isUnexpectedError && (
                    <Caption style={styles.error}>
                        {t`Oops. Something went wrong. Please try again.`}
                    </Caption>
                )}
            </View>
        );
    };

    const {muteVideos, showCaptions, playbackRate} = currentSettings;

    return (
        <View role="region" aria-label={t`Video settings`}>
            <StudentVideoSettings
                muteVideos={muteVideos}
                showCaptions={showCaptions}
                playbackRate={playbackRate}
                disabled={false}
                onChangeMuteVideos={(muteVideos) => {
                    onSettingsChange({muteVideos});
                }}
                onChangeShowCaptions={(showCaptions) => {
                    onSettingsChange({showCaptions});
                }}
                onChangePlaybackRate={(playbackRate) => {
                    onSettingsChange({playbackRate});
                }}
            />
            <View style={{marginBlockStart: sizing.size_240}} />
            {renderSaveButton()}
        </View>
    );
};

const styles = StyleSheet.create({
    buttonContainer: {
        flexDirection: "row",
    },
    button: {
        alignSelf: "flex-start",
        paddingInlineStart: sizing.size_160,
        paddingInlineEnd: sizing.size_160,
    },
    error: {
        color: semanticColor.status.critical.foreground,
        marginInlineStart: sizing.size_160,
    },
});

export default React.memo(VideoSettings);
